export type PlanoTipo = 'individual' | 'familiar';
export type PlanoPeriodicidade = 'mensal' | 'trimestral' | 'semestral' | 'anual';

export interface PlanoGradeApi {
  id: number;
  titulo: string;
  descricao?: string;
  valor: string | number;
  tipo?: string;
  periodicidade?: string;
  quantidade_maxima_usuarios?: number | string | null;
  economia_percentual?: number | null;
  status?: string;
}

export interface PlanoGradeView {
  id: number;
  titulo: string;
  descricao: string;
  valor: string;
  tipo: PlanoTipo;
  periodicidade: PlanoPeriodicidade;
  quantidade_maxima_usuarios: number;
  economiaPercentual: number | null;
  periodLabel: string;
  priceLabel: string;
  popular: boolean;
  badge: string | null;
  economiaLabel: string | null;
  subtitle: string;
  features: readonly string[];
  ctaLabel: string;
  participantsLabel: string;
}

export interface GrupoPlanosGrade {
  tipo: PlanoTipo;
  titulo: string;
  subtitle: string;
  planos: PlanoGradeView[];
}

const ORDEM_PERIODICIDADE: PlanoPeriodicidade[] = ['mensal', 'trimestral', 'semestral', 'anual'];

const BENEFICIOS: Record<PlanoTipo, readonly string[]> = {
  individual: [
    'Centenas de parceiros',
    'Descontos de até 80%',
    'Gastronomia, saúde, moda e mais',
    'Telemedicina com 15+ especialidades',
    'Utilização ilimitada',
  ],
  familiar: [
    'Tudo do plano Individual',
    'Até 4 pessoas no mesmo plano',
    'Telemedicina para todos os membros',
    'Sorteios mensais exclusivos',
    'Atendimento prioritário 24/7',
  ],
};

export function normalizarTipo(valor: unknown): PlanoTipo | null {
  const t = String(valor ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (t === 'individual') return 'individual';
  if (t === 'familiar' || t === 'familia') return 'familiar';
  return null;
}

export function normalizarPeriodicidade(valor: unknown): PlanoPeriodicidade {
  const p = String(valor ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (p === 'trimestral') return 'trimestral';
  if (p === 'semestral') return 'semestral';
  if (p === 'anual') return 'anual';
  return 'mensal';
}

export function labelPeriodicidade(periodicidade: string): string {
  const p = normalizarPeriodicidade(periodicidade);
  if (p === 'trimestral') return 'trimestre';
  if (p === 'semestral') return 'semestre';
  if (p === 'anual') return 'ano';
  return 'mês';
}

export function mesesDaPeriodicidade(periodicidade: string): number {
  const p = normalizarPeriodicidade(periodicidade);
  if (p === 'trimestral') return 3;
  if (p === 'semestral') return 6;
  if (p === 'anual') return 12;
  return 1;
}

export function formatarPreco(valor: string | number): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return String(valor ?? '');
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function calcularEconomiaPercentual(
  plano: PlanoGradeApi,
  grade: readonly PlanoGradeApi[]
): number | null {
  if (plano.economia_percentual != null && Number.isFinite(Number(plano.economia_percentual))) {
    const enviado = Math.round(Number(plano.economia_percentual));
    return enviado > 0 ? enviado : null;
  }

  const tipo = normalizarTipo(plano.tipo);
  const periodicidade = normalizarPeriodicidade(plano.periodicidade);
  if (!tipo || periodicidade === 'mensal') return null;

  const valor = Number(plano.valor);
  if (!Number.isFinite(valor) || valor <= 0) return null;

  const mensal = grade.find((candidato) => {
    return (
      normalizarTipo(candidato.tipo) === tipo &&
      normalizarPeriodicidade(candidato.periodicidade) === 'mensal' &&
      String(candidato.status ?? 'Ativo').toLowerCase() !== 'inativo'
    );
  });
  const valorMensal = Number(mensal?.valor ?? 0);
  if (!Number.isFinite(valorMensal) || valorMensal <= 0) return null;

  const equivalente = valorMensal * mesesDaPeriodicidade(periodicidade);
  if (equivalente <= 0) return null;
  const pct = Math.round((1 - valor / equivalente) * 100);
  return pct > 0 ? pct : null;
}

export function beneficiosPorTipo(tipo: PlanoTipo): readonly string[] {
  return BENEFICIOS[tipo];
}

export function toPlanoGradeView(plano: PlanoGradeApi, grade: readonly PlanoGradeApi[]): PlanoGradeView | null {
  const tipo = normalizarTipo(plano.tipo);
  if (!tipo) return null;

  const periodicidade = normalizarPeriodicidade(plano.periodicidade);
  const economia = calcularEconomiaPercentual(plano, grade);
  const popular = periodicidade === 'anual';
  const maxUsuarios = Number(plano.quantidade_maxima_usuarios ?? (tipo === 'familiar' ? 4 : 1));

  return {
    id: Number(plano.id ?? 0),
    titulo: String(plano.titulo ?? ''),
    descricao: String(plano.descricao ?? ''),
    valor: String(plano.valor ?? '0'),
    tipo,
    periodicidade,
    quantidade_maxima_usuarios: Number.isFinite(maxUsuarios) && maxUsuarios > 0 ? maxUsuarios : tipo === 'familiar' ? 4 : 1,
    economiaPercentual: economia,
    periodLabel: labelPeriodicidade(periodicidade),
    priceLabel: formatarPreco(plano.valor),
    popular,
    badge: popular ? 'Mais escolhido' : null,
    economiaLabel: economia != null ? `Economize ${economia}%` : null,
    subtitle: tipo === 'familiar' ? 'Até 4 pessoas' : 'Para você',
    features: beneficiosPorTipo(tipo),
    ctaLabel: tipo === 'familiar' ? 'Escolher Familiar' : 'Escolher Individual',
    participantsLabel: tipo === 'familiar' ? 'Até 4 pessoas' : '1 participante',
  };
}

export function agruparPlanosPorTipo(planosApi: readonly PlanoGradeApi[]): GrupoPlanosGrade[] {
  const views = planosApi
    .map((plano) => toPlanoGradeView(plano, planosApi))
    .filter((plano): plano is PlanoGradeView => plano != null && plano.id > 0);

  const grupos: GrupoPlanosGrade[] = [
    {
      tipo: 'individual',
      titulo: 'Individual',
      subtitle: 'Para você',
      planos: [],
    },
    {
      tipo: 'familiar',
      titulo: 'Familiar',
      subtitle: 'Até 4 pessoas',
      planos: [],
    },
  ];

  for (const view of views) {
    const grupo = grupos.find((item) => item.tipo === view.tipo);
    grupo?.planos.push(view);
  }

  for (const grupo of grupos) {
    grupo.planos.sort(
      (a, b) => ORDEM_PERIODICIDADE.indexOf(a.periodicidade) - ORDEM_PERIODICIDADE.indexOf(b.periodicidade)
    );
  }

  return grupos.filter((grupo) => grupo.planos.length > 0);
}

export function planoIndividualMensal(grupos: readonly GrupoPlanosGrade[]): PlanoGradeView | null {
  const individual = grupos.find((grupo) => grupo.tipo === 'individual');
  return individual?.planos.find((plano) => plano.periodicidade === 'mensal') ?? individual?.planos[0] ?? null;
}
